import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Chip,
    Box,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField
} from '@mui/material';
import { useNotify } from 'react-admin';
import { getTimeoffApprovals, approveOrRejectRequest, TimeoffRequest } from '../dataProvider';

export const TimeoffApprovalList: React.FC = () => {
    const notify = useNotify();
    const [requests, setRequests] = useState<TimeoffRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [actionDialogOpen, setActionDialogOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState<TimeoffRequest | null>(null);
    const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
    const [reviewComment, setReviewComment] = useState('');

    const fetchRequests = async () => {
        setLoading(true);
        try {
            const data = await getTimeoffApprovals();
            setRequests(data);
        } catch (error) {
            console.error('Error fetching timeoff requests:', error);
            notify('Failed to load requests', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequests();
    }, []);

    const handleAction = async () => {
        if (!selectedRequest || !actionType) return;

        try {
            await approveOrRejectRequest(selectedRequest.id, actionType, reviewComment);
            notify(`Request ${actionType}d successfully`, { type: 'success' });
            fetchRequests();
        } catch (error: any) {
            const errorMessage = error?.body?.message || `Failed to ${actionType} request`;
            notify(errorMessage, { type: 'error' });
        } finally {
            setActionDialogOpen(false);
            setSelectedRequest(null);
            setActionType(null);
            setReviewComment('');
        }
    };

    const formatPartialDay = (partial: string) => {
        switch (partial) {
            case 'FirstHalf': return 'First Half';
            case 'SecondHalf': return 'Second Half';
            default: return 'Full Day';
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Card>
            <CardContent>
                <Typography variant="h5" gutterBottom>
                    Time-Off Requests for Approval
                </Typography>

                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Employee</TableCell>
                                <TableCell>Leave Type</TableCell>
                                <TableCell>Start Date</TableCell>
                                <TableCell>End Date</TableCell>
                                <TableCell>Work Days</TableCell>
                                <TableCell>Note</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {requests.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        No pending requests
                                    </TableCell>
                                </TableRow>
                            ) : (
                                requests.map((request) => (
                                    <TableRow key={request.id}>
                                        <TableCell>
                                            {request.full_name || request.employee_id}
                                            {request.email && (
                                                <Typography variant="caption" display="block">
                                                    {request.email}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={request.leave_type}
                                                color={request.leave_type === 'Annual' ? 'primary' : 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {new Date(request.start_date).toLocaleDateString(undefined, { timeZone: 'UTC' })}
                                            {request.start_partial !== 'Full' && (
                                                <Typography variant="caption" display="block">
                                                    ({formatPartialDay(request.start_partial)})
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {new Date(request.end_date).toLocaleDateString(undefined, { timeZone: 'UTC' })}
                                            {request.end_partial !== 'Full' && (
                                                <Typography variant="caption" display="block">
                                                    ({formatPartialDay(request.end_partial)})
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>{request.work_days}</TableCell>
                                        <TableCell>
                                            {request.note || '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" gap={1}>
                                                <Button
                                                    size="small"
                                                    color="success"
                                                    variant="contained"
                                                    onClick={() => {
                                                        setSelectedRequest(request);
                                                        setActionType('approve');
                                                        setActionDialogOpen(true);
                                                    }}
                                                >
                                                    Approve
                                                </Button>
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    variant="outlined"
                                                    onClick={() => {
                                                        setSelectedRequest(request);
                                                        setActionType('reject');
                                                        setActionDialogOpen(true);
                                                    }}
                                                >
                                                    Reject
                                                </Button>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>

            <Dialog
                open={actionDialogOpen}
                onClose={() => setActionDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {actionType === 'approve' ? 'Approve' : 'Reject'} Time-Off Request
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {actionType === 'approve'
                            ? `Are you sure you want to approve this time-off request for ${selectedRequest?.work_days} work days?`
                            : 'Are you sure you want to reject this time-off request?'}
                    </DialogContentText>
                    {actionType === 'reject' && (
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Reason for Rejection (Optional)"
                            fullWidth
                            multiline
                            rows={3}
                            value={reviewComment}
                            onChange={(e) => setReviewComment(e.target.value)}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setActionDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAction}
                        color={actionType === 'approve' ? 'success' : 'error'}
                        variant="contained"
                        autoFocus
                    >
                        {actionType === 'approve' ? 'Approve' : 'Reject'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};
